import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Paragraph } from '@digdir/designsystemet-react';
import type { Action } from '../hmsActions';
import { ApprovalComment } from './ApprovalChoice';
import layout from '../hmsPage.module.css';
import classes from './ApprovalChoice.module.css';

type Props = {
  id: string;
  approve: Action<[comment: string]>;
  withdraw: Action;
  /** The HR user is also a main safety representative and made the version, so another one must approve it */
  ownVersion?: boolean;
};

/** HR can approve a version that waits for the safety representative, but must say why */
export const PendingActions = ({ id, approve, withdraw, ownVersion = false }: Props): ReactElement => {
  const { t } = useTranslation();
  const [approving, setApproving] = useState(false);
  const [comment, setComment] = useState('');
  const [tried, setTried] = useState(false);

  const confirm = () => {
    if (!comment.trim()) {
      setTried(true);
      document.getElementById(id)?.focus();
      return;
    }
    approve.run(comment.trim());
  };

  // No one approves their own work in another role, so the version does not wait for them as main safety representative
  const own = ownVersion && <Paragraph data-size="sm">{t('hms.pendingApproval.ownVersion')}</Paragraph>;

  if (!approving) {
    return (
      <>
        {own}
        <span className={layout.actions} data-gap="sm">
          <Button variant="secondary" data-size="sm" onClick={() => setApproving(true)}>
            {t('hms.pendingApproval.markApproved')}
          </Button>
          <Button variant="tertiary" data-size="sm" onClick={withdraw.run} loading={withdraw.pending}>
            {t('hms.pendingApproval.withdraw')}
          </Button>
        </span>
      </>
    );
  }

  return (
    <div className={classes.fieldset}>
      {own}
      <ApprovalComment
        id={id}
        label={t('hms.pendingApproval.comment')}
        description={t('hms.pendingApproval.commentDescription')}
        value={comment}
        onChange={setComment}
        error={tried && !comment.trim() ? t('hms.approval.commentError') : undefined}
      />
      <span className={layout.actions} data-gap="sm">
        <Button variant="secondary" data-size="sm" onClick={confirm} loading={approve.pending}>
          {t('hms.pendingApproval.confirm')}
        </Button>
        <Button
          variant="tertiary"
          data-size="sm"
          onClick={() => {
            setApproving(false);
            setTried(false);
          }}
        >
          {t('hms.pendingApproval.cancel')}
        </Button>
      </span>
    </div>
  );
};
